import React, { createContext, useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import PropTypes from "prop-types";

import { getIsIslamicBanking } from "../../store/selectors/appConfig";
import { LOGO_ISLAMIC, LOGO_STANDART } from "../../components/Header/constants";

export const LogoTypeContext = createContext({});

let setValues;

/**
 * Returns the default logo type based on whether Islamic banking is enabled.
 * @param {boolean} isIslamicBanking - Indicates if Islamic banking is enabled.
 * @returns {string} The logo type constant.
 */
const getDefaultLogoType = isIslamicBanking => {
  if (isIslamicBanking) {
    return LOGO_ISLAMIC;
  }
  return LOGO_STANDART;
};

/**
 * LogoTypeProvider - Provides the logo type context to child components.
 * @component
 * @param {object} props - Component props
 * @param {React.ReactNode} props.children - Child components
 * @returns {React.ReactElement}
 */
export const LogoTypeProvider = ({ children }) => {
  const { pathname } = useLocation();
  const isIslamicBanking = useSelector(getIsIslamicBanking);
  const [contextValue, setContextValue] = useState(null);
  setValues = setContextValue;

  useEffect(() => {
    contextValue && setContextValue(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  return (
    <LogoTypeContext.Provider value={contextValue || getDefaultLogoType(isIslamicBanking)}>
      {children}
    </LogoTypeContext.Provider>
  );
};

LogoTypeProvider.propTypes = {
  children: PropTypes.node.isRequired
};

/**
 * useLogoType - Custom hook to update the logo type in context.
 * @param {string} logo - The logo type to set in context.
 */
export const useLogoType = logo => {
  useEffect(() => {
    if (typeof setValues === "function") {
      setValues(logo);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [logo]);
};
